import { useCallback, useEffect, useRef, useState } from 'react';

function defaultFormat(value) {
  return value ?? '';
}

export function useDraftField({
  value,
  onCommit,
  format = defaultFormat,
  areEqual = Object.is,
  unmountPolicy = 'cancel',
}) {
  const [draft, setDraft] = useState(() => format(value));
  const [isDirty, setIsDirty] = useState(false);
  const timerRef = useRef(null);
  const draftRef = useRef(format(value));
  const dirtyRef = useRef(false);
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);

  const emitCommit = useCallback((nextValue) => {
    const canonicalValue = format(valueRef.current);
    if (!areEqual(nextValue, canonicalValue)) {
      onCommitRef.current(nextValue);
    }
  }, [areEqual, format]);

  const clearPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commitValue = useCallback((nextValue) => {
    clearPending();
    dirtyRef.current = false;
    setIsDirty(false);
    emitCommit(nextValue);
  }, [clearPending, emitCommit]);

  useEffect(() => {
    valueRef.current = value;
    if (!isDirty) {
      const nextDraft = format(value);
      draftRef.current = nextDraft;
      dirtyRef.current = false;
      setDraft(nextDraft);
    }
  }, [format, isDirty, value]);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => () => {
    if (unmountPolicy === 'flush' && dirtyRef.current) {
      clearPending();
      emitCommit(draftRef.current);
      dirtyRef.current = false;
      return;
    }
    clearPending();
    dirtyRef.current = false;
  }, [clearPending, emitCommit, unmountPolicy]);

  const updateDraft = useCallback((nextValue) => {
    clearPending();
    draftRef.current = nextValue;
    dirtyRef.current = true;
    setDraft(nextValue);
    setIsDirty(true);
  }, [clearPending]);

  const scheduleCommit = useCallback((nextValue, delayMs) => {
    draftRef.current = nextValue;
    dirtyRef.current = true;
    setDraft(nextValue);
    setIsDirty(true);
    clearPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      commitValue(nextValue);
    }, delayMs);
  }, [clearPending, commitValue]);

  const commit = useCallback((nextValue = draft) => {
    commitValue(nextValue);
  }, [commitValue, draft]);

  const flush = useCallback(() => {
    if (!dirtyRef.current) return;
    commitValue(draftRef.current);
  }, [commitValue]);

  const cancel = useCallback(() => {
    clearPending();
    dirtyRef.current = false;
    setIsDirty(false);
    const nextDraft = format(valueRef.current);
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  }, [clearPending, format]);

  const reset = useCallback((nextValue = valueRef.current) => {
    clearPending();
    dirtyRef.current = false;
    setIsDirty(false);
    const nextDraft = format(nextValue);
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  }, [clearPending, format]);

  return {
    draft,
    isDirty,
    updateDraft,
    scheduleCommit,
    commit,
    flush,
    cancel,
    clearPending,
    reset,
  };
}
